import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/lib/api";
import { Buildings } from "@phosphor-icons/react";

export default function RegisterPage() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "" });
  const [loading, setLoading] = useState(false);

  const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) return toast.error("Le mot de passe doit avoir au moins 6 caractères");
    setLoading(true);
    try {
      await register(form);
      toast.success("Compte créé !");
      navigate("/", { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur d'inscription");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto grid min-h-[80vh] max-w-6xl gap-8 px-4 py-10 md:grid-cols-2 md:px-8 md:py-16">
      <div className="hidden flex-col justify-between rounded-3xl bg-primary p-10 text-primary-foreground md:flex">
        <div className="flex items-center gap-2">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-background text-foreground">
            <Buildings size={22} weight="duotone" />
          </div>
          <span className="font-display text-xl font-black">BODJE IMMO GUINÉE</span>
        </div>
        <div>
          <h2 className="font-display text-4xl font-black leading-tight">
            Rejoignez la plus grande communauté immobilière de Guinée.
          </h2>
          <p className="mt-4 text-white/85">
            Publiez, achetez, louez — et échangez directement avec les propriétaires.
          </p>
        </div>
        <div className="text-xs uppercase tracking-widest text-white/60">100% Made in Guinée</div>
      </div>

      <div className="flex flex-col justify-center">
        <div className="mb-8">
          <span className="text-xs font-bold uppercase tracking-[0.25em] text-primary">Inscription</span>
          <h1 className="mt-2 font-display text-3xl font-black md:text-4xl">Créer un compte</h1>
          <p className="mt-2 text-muted-foreground">
            Déjà membre ?{" "}
            <Link to="/login" className="font-bold text-primary hover:underline" data-testid="go-login">
              Se connecter
            </Link>
          </p>
        </div>


        <form onSubmit={submit} className="space-y-4" data-testid="register-form">
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider">Nom complet</Label>
            <Input
              data-testid="register-name"
              value={form.name}
              onChange={(e) => update("name", e.target.value)}
              required
              className="mt-2 h-12"
              placeholder="Ex: Mamadou Diallo"
            />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider">Email</Label>
            <Input
              type="email"
              data-testid="register-email"
              value={form.email}
              onChange={(e) => update("email", e.target.value)}
              required
              className="mt-2 h-12"
              placeholder="vous@exemple.com"
            />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider">Téléphone (WhatsApp)</Label>
            <Input
              data-testid="register-phone"
              value={form.phone}
              onChange={(e) => update("phone", e.target.value)}
              className="mt-2 h-12"
              placeholder="+224 620 00 00 00"
            />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider">Mot de passe</Label>
            <Input
              type="password"
              data-testid="register-password"
              value={form.password}
              onChange={(e) => update("password", e.target.value)}
              required
              minLength={6}
              className="mt-2 h-12"
              placeholder="Min. 6 caractères"
            />
          </div>
          <Button
            type="submit"
            disabled={loading}
            data-testid="register-submit"
            className="btn-pill h-12 w-full rounded-full bg-primary font-bold text-primary-foreground hover:bg-primary/90"
          >
            {loading ? "Création…" : "Créer mon compte"}
          </Button>
        </form>
      </div>
    </div>
  );
}
